import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ToastData } from "./Toast";

interface ModalState {
  readonly id: number;
  readonly content: ReactNode;
  readonly titleId: string;
}

interface OverlayContextValue {
  readonly toast: ToastData | null;
  readonly showToast: (toast: ToastData) => void;
  readonly clearToast: () => void;
  readonly modal: ModalState | null;
  readonly openModal: (content: ReactNode, titleId: string) => void;
  readonly closeModal: () => void;
  readonly popoverOpen: boolean;
  readonly openPopover: () => void;
  readonly closePopover: () => void;
}

const OverlayContext = createContext<OverlayContextValue | null>(null);

const PASS_TOAST_AUTO_DISMISS_MS = 2400;
let nextModalId = 1;

/**
 * オーバーレイの同時表示ルールを一元管理する（screen-flow.md §9.2）。
 * - モーダルは同時に1つだけ。新しいモーダルを開く要求が来たら先のモーダルを置き換える
 * - ポップオーバーはモーダルが開いた時点で閉じる
 * - トーストは同時に1つだけ。新着は既存を置き換える（キューを積まない）
 * - 合格等の成功系トーストのみ2.4秒で自動的に消える（ui-design.md §5.8）
 */
export function OverlayProvider({ children }: { readonly children: ReactNode }) {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const clearToast = useCallback(() => setToast(null), []);

  const showToast = useCallback((next: ToastData) => {
    setToast(next);
  }, []);

  useEffect(() => {
    if (toast?.kind !== "pass") {
      return;
    }
    const timer = window.setTimeout(clearToast, PASS_TOAST_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [toast, clearToast]);

  const openModal = useCallback((content: ReactNode, titleId: string) => {
    nextModalId += 1;
    setModal({ id: nextModalId, content, titleId });
    setPopoverOpen(false);
  }, []);

  const closeModal = useCallback(() => setModal(null), []);
  const openPopover = useCallback(() => setPopoverOpen(true), []);
  const closePopover = useCallback(() => setPopoverOpen(false), []);

  const value = useMemo<OverlayContextValue>(
    () => ({
      toast,
      showToast,
      clearToast,
      modal,
      openModal,
      closeModal,
      popoverOpen,
      openPopover,
      closePopover,
    }),
    [
      toast,
      showToast,
      clearToast,
      modal,
      openModal,
      closeModal,
      popoverOpen,
      openPopover,
      closePopover,
    ],
  );

  return <OverlayContext.Provider value={value}>{children}</OverlayContext.Provider>;
}

export function useOverlay(): OverlayContextValue {
  const context = useContext(OverlayContext);
  if (!context) {
    throw new Error("useOverlay は OverlayProvider の内側でのみ使用できます");
  }
  return context;
}
